"use client";

import { useMemo, useState } from "react";
import {
  Checkbox,
  CopyButton,
  Panel,
  PillGroup,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { CvPreview } from "@/components/tools/admissions/cv-preview";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  buildCv,
  CV_SECTIONS,
  type CvInput,
  type CvNumbering,
  type CvSectionId,
} from "@/lib/tool-kit/admissions/academic-cv";
import {
  cvToHtmlBody,
  cvToLatex,
  cvToMarkdown,
  cvToText,
  cvToWordHtml,
} from "@/lib/tool-kit/admissions/academic-cv-export";
import { downloadText } from "@/lib/tool-kit/download";

/** English strings. A translated content file overrides these through `ui`. */
export const ACADEMIC_CV_GENERATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  aboutHeading: "About you",
  nameLabel: "Full name",
  namePlaceholder: "Adaeze Okafor",
  titleLabel: "Position",
  titlePlaceholder: "Associate Professor of Marine Science",
  affiliationLabel: "Institution and department",
  affiliationPlaceholder: "Department of Marine Science, University of Lagos",
  emailLabel: "Email",
  emailPlaceholder: "a.okafor@example.org",
  phoneLabel: "Phone (optional)",
  websiteLabel: "Website (optional)",
  websitePlaceholder: "https://example.org/okafor",
  orcidLabel: "ORCID iD (optional)",
  orcidPlaceholder: "0000-0002-1825-0097",
  addressLabel: "Address (optional)",
  sectionsHeading: "Sections",
  sectionsNote:
    "One entry per line. Start a line with a date and a pipe — “2019–2023 | PhD, University of Lagos” — or just a year or range. A line starting with a dash adds a detail to the entry above. Empty sections are left out.",
  referencesNote:
    "Paste or type each reference already formatted in your field’s style, one per line. The tool never fetches or reformats them. Put *asterisks* round anything to italicise.",
  labels: {
    interests: "Research interests",
    education: "Education",
    appointments: "Academic appointments",
    publications: "Peer-reviewed publications",
    chapters: "Books and chapters",
    conferences: "Conference papers",
    grants: "Grants and fellowships",
    awards: "Honors and awards",
    teaching: "Teaching",
    talks: "Invited talks",
    service: "Service",
  },
  placeholders: {
    interests:
      "Coral reef resilience, ocean warming, community-based monitoring",
    education:
      "2009–2014 | PhD in Marine Ecology, University of Lagos\n2005–2009 | BSc in Zoology, University of Ibadan",
    appointments:
      "2018–present | Associate Professor, University of Lagos\n2014–2018 | Lecturer, University of Lagos",
    publications:
      "Okafor, A. (2025). Reef recovery after bleaching. *Journal of Marine Ecology*, 41(2), 112–130.",
    chapters:
      "Bello, T., & Okafor, A. (2024). Monitoring reefs with fishers. In *Coastal Futures* (pp. 45–60). Example Press.",
    conferences:
      "Okafor, A. (2023, June). Recovery after bleaching [Conference presentation]. Marine Science Congress, Accra, Ghana.",
    grants:
      "2022–2026 | Gulf of Guinea Reef Recovery Project, PI, funded by an example national research council",
    awards: "2024 | National early-career research prize",
    teaching:
      "2018–present | Marine Ecology (undergraduate), Coral Reef Ecology (graduate)",
    talks: "2025 | Invited seminar, Department of Biology, University of Accra",
    service: "2021–present | Editorial board, Journal of Marine Ecology",
  },
  optionsHeading: "Layout",
  sortLabel: "Sort every section newest first",
  sortHint:
    "Dated entries sort by end year (present counts as newest); references sort by the year found in them. Undated lines stay in the order typed, after the dated ones.",
  numberingLabel: "Number the publications",
  numberingOptions: {
    none: "No numbers",
    numbered: "1 = newest",
    reverse: "Newest has the highest number",
  },
  boldNameLabel: "Your name as it appears in references (optional)",
  boldNamePlaceholder: "Okafor, A.",
  boldNameHint: "Set in bold wherever it appears in a reference.",
  previewHeading: "Your CV",
  emptyNote: "Add some sections above and your CV appears here.",
  copy: "Copy for Word or Google Docs",
  copied: "Copied!",
  downloadWord: "Download Word (.doc)",
  downloadMarkdown: "Download Markdown (.md)",
  downloadLatex: "Download LaTeX (.tex)",
  downloadText: "Download plain text (.txt)",
  wordNote:
    "The .doc file is HTML that Word opens directly; use Save As to keep it as .docx.",
  fileName: "academic-cv",
  placeholderName: "Your Name",
  orcidPrefix: "ORCID:",
  sectionCount: "one:{count} section|other:{count} sections",
  entryCount: "one:{count} entry|other:{count} entries",
  summary: "{sections}, {entries}.",
  privacy: "Everything stays in your browser. Nothing is uploaded or saved.",
  example: {
    name: "Adaeze Okafor",
    title: "Associate Professor of Marine Science",
    affiliation: "Department of Marine Science, University of Lagos",
    email: "a.okafor@example.org",
    orcid: "0000-0002-1825-0097",
    boldName: "Okafor, A.",
    interests:
      "Coral reef resilience, ocean warming, community-based reef monitoring",
    education:
      "2005–2009 | BSc in Zoology, University of Ibadan\n2009–2014 | PhD in Marine Ecology, University of Lagos\n  - Thesis: Recovery of reef corals after bleaching events",
    appointments:
      "2014–2018 | Lecturer, University of Lagos\n2018–present | Associate Professor, University of Lagos",
    publications:
      "Okafor, A., & Bello, T. (2023). Who counts the fish? Community monitoring on West African reefs. *Journal of Marine Ecology*, 40(1), 1–20.\nOkafor, A. (2025). Reef recovery after repeated bleaching. *Journal of Marine Ecology*, 41(2), 112–130.",
    grants:
      "2022–2026 | Gulf of Guinea Reef Recovery Project, principal investigator",
    awards: "2024 | National early-career research prize",
    teaching:
      "2018–present | Marine Ecology (undergraduate); Coral Reef Ecology (graduate)",
    service: "2021–present | Editorial board, Journal of Marine Ecology",
  },
} as const;

const EMPTY_SECTIONS = Object.fromEntries(
  CV_SECTIONS.map((id) => [id, ""])
) as Record<CvSectionId, string>;

const EMPTY: CvInput = {
  name: "",
  title: "",
  affiliation: "",
  email: "",
  phone: "",
  website: "",
  orcid: "",
  address: "",
  sections: EMPTY_SECTIONS,
  sortNewestFirst: true,
  numbering: "numbered",
  boldName: "",
};

type InfoKey = Exclude<
  keyof CvInput,
  "sections" | "sortNewestFirst" | "numbering"
>;

const REFERENCE_SECTIONS: readonly CvSectionId[] = [
  "publications",
  "chapters",
  "conferences",
];

export default function AcademicCvGeneratorWidget() {
  const strings = useToolUi(ACADEMIC_CV_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [input, setInput] = useState<CvInput>(EMPTY);
  const labels = useMemo(
    () => ({
      placeholderName: strings.placeholderName,
      orcid: strings.orcidPrefix,
      headings: strings.labels,
    }),
    [strings]
  );
  const model = useMemo(() => buildCv(input, labels), [input, labels]);
  const info = (key: InfoKey, id: string) => ({
    id: `acv-${id}`,
    onChange: (value: string) =>
      setInput((previous) => ({ ...previous, [key]: value })),
    value: input[key],
  });
  const entryCount = model.sections.reduce(
    (total, section) => total + section.entries.length,
    0
  );
  const hasContent = model.sections.length > 0;
  const save = (extension: string, text: string, mime: string) =>
    downloadText(`${strings.fileName}.${extension}`, text, mime);

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            const {
              interests,
              education,
              appointments,
              publications,
              grants,
              awards,
              teaching,
              service,
              ...about
            } = strings.example;
            setInput({
              ...EMPTY,
              ...about,
              sections: {
                ...EMPTY_SECTIONS,
                interests,
                education,
                appointments,
                publications,
                grants,
                awards,
                teaching,
                service,
              },
            });
          }}
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setInput(EMPTY)}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      <Panel title={strings.aboutHeading}>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            {...info("name", "name")}
            label={strings.nameLabel}
            placeholder={strings.namePlaceholder}
          />
          <TextField
            {...info("title", "title")}
            label={strings.titleLabel}
            placeholder={strings.titlePlaceholder}
          />
          <TextField
            {...info("affiliation", "affiliation")}
            label={strings.affiliationLabel}
            placeholder={strings.affiliationPlaceholder}
          />
          <TextField
            {...info("email", "email")}
            label={strings.emailLabel}
            placeholder={strings.emailPlaceholder}
          />
          <TextField {...info("phone", "phone")} label={strings.phoneLabel} />
          <TextField
            {...info("website", "website")}
            label={strings.websiteLabel}
            placeholder={strings.websitePlaceholder}
          />
          <TextField
            {...info("orcid", "orcid")}
            label={strings.orcidLabel}
            placeholder={strings.orcidPlaceholder}
          />
          <TextField
            {...info("address", "address")}
            label={strings.addressLabel}
          />
        </div>
      </Panel>

      <Panel title={strings.sectionsHeading}>
        <p className={HINT}>{strings.sectionsNote}</p>
        {CV_SECTIONS.map((id) => (
          <TextAreaField
            hint={
              REFERENCE_SECTIONS.includes(id)
                ? strings.referencesNote
                : undefined
            }
            id={`acv-section-${id}`}
            key={id}
            label={strings.labels[id]}
            onChange={(value) =>
              setInput((previous) => ({
                ...previous,
                sections: { ...previous.sections, [id]: value },
              }))
            }
            placeholder={strings.placeholders[id]}
            prose
            rows={id === "interests" ? 2 : 4}
            value={input.sections[id]}
          />
        ))}
      </Panel>

      <Panel title={strings.optionsHeading}>
        <Checkbox
          checked={input.sortNewestFirst}
          label={strings.sortLabel}
          onChange={(sortNewestFirst) =>
            setInput((previous) => ({ ...previous, sortNewestFirst }))
          }
        />
        <p className={HINT}>{strings.sortHint}</p>
        <PillGroup
          label={strings.numberingLabel}
          name="acv-numbering"
          onChange={(numbering: CvNumbering) =>
            setInput((previous) => ({ ...previous, numbering }))
          }
          options={(["none", "numbered", "reverse"] as const).map((value) => ({
            value,
            label: strings.numberingOptions[value],
          }))}
          value={input.numbering}
        />
        <TextField
          {...info("boldName", "bold-name")}
          hint={strings.boldNameHint}
          label={strings.boldNameLabel}
          placeholder={strings.boldNamePlaceholder}
        />
      </Panel>

      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className={WIDGET_H2}>{strings.previewHeading}</h2>
          <div className="flex flex-wrap gap-2">
            <CopyButton
              copiedLabel={strings.copied}
              disabled={!hasContent}
              html={cvToHtmlBody(model, input.boldName)}
              label={strings.copy}
              small
              text={cvToText(model)}
            />
            {(
              [
                [
                  "doc",
                  strings.downloadWord,
                  "application/msword",
                  () => cvToWordHtml(model, input.boldName),
                ],
                [
                  "md",
                  strings.downloadMarkdown,
                  "text/markdown;charset=utf-8",
                  () => cvToMarkdown(model, input.boldName),
                ],
                [
                  "tex",
                  strings.downloadLatex,
                  "application/x-tex;charset=utf-8",
                  () => cvToLatex(model, input.boldName),
                ],
                [
                  "txt",
                  strings.downloadText,
                  "text/plain;charset=utf-8",
                  () => cvToText(model),
                ],
              ] as const
            ).map(([extension, label, mime, build]) => (
              <button
                className={BUTTON_SMALL}
                disabled={!hasContent}
                key={extension}
                onClick={() => save(extension, build(), mime)}
                type="button"
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <p aria-live="polite" className={HINT}>
          {hasContent
            ? t(strings.summary, {
                sections: tPlural(
                  strings.sectionCount,
                  locale,
                  model.sections.length,
                  { count: fmt(model.sections.length) }
                ),
                entries: tPlural(strings.entryCount, locale, entryCount, {
                  count: fmt(entryCount),
                }),
              })
            : strings.emptyNote}
        </p>
        <CvPreview boldName={input.boldName} model={model} />
        <p className={HINT}>{strings.wordNote}</p>
        <p className={HINT}>{strings.privacy}</p>
      </section>
    </div>
  );
}
